/**
 * XRPerfMonitorSystem — monitors XR frame timing and GPU budget.
 *
 * Phase: UI, Priority: 50
 * Tracks wall-clock frame intervals against the session's display rate and
 * emits xr:perf:warning when the budget is exceeded for consecutive frames.
 */

import type { ISystem } from '../ecs/System.js';
import type { World } from '../ecs/World.js';
import { LoopPhase, SubPhase } from '../shared/types.js';
import type { XRSessionState } from './types.js';
import type { EngineRenderer } from '../render/Renderer.js';
import type { EventBus } from '../core/EventBus.js';
import { Logger } from '../core/Logger.js';

const OVERSHOOT_FRAMES = 10; // Consecutive overshoots before warning
const DEFAULT_TARGET_HZ = 90;

export class XRPerfMonitorSystem implements ISystem {
  readonly name = 'XRPerfMonitor';
  readonly phase = LoopPhase.UI;
  readonly subPhase = SubPhase.Update;
  readonly priority = 50;

  private lastFrameTime = 0;
  private consecutiveOvershoots = 0;
  private frameBudgetMs: number;
  /** Rate the current budget was derived from (Hz); 0 = default/explicit. */
  private budgetRateHz = 0;
  /** Caller pinned targetHz explicitly — never override it from the session. */
  private readonly explicitBudget: boolean;
  private renderScale = 1.0;
  private autoScale: boolean;
  private warnedScaleNoop = false;

  constructor(
    private state: XRSessionState,
    private renderer: EngineRenderer,
    private events: EventBus,
    options?: { targetHz?: number; autoScale?: boolean },
  ) {
    this.explicitBudget = options?.targetHz !== undefined;
    this.frameBudgetMs = 1000 / (options?.targetHz ?? DEFAULT_TARGET_HZ);
    this.autoScale = options?.autoScale ?? false;
  }

  execute(_dt: number, _world: World): void {
    if (!this.state.isPresenting) {
      this.consecutiveOvershoots = 0;
      this.lastFrameTime = 0;
      this.renderScale = 1.0;
      this.budgetRateHz = 0;
      return;
    }

    // Budget from the session's REAL display rate. The fixed 90 Hz default
    // flagged every frame on a 72 Hz session (Quest 2 default, Quest 3 until
    // updateTargetFrameRate) — 13.9 ms intervals against an 11.1 ms budget —
    // and never fired at 120 Hz. `XRSession.frameRate` changes with
    // updateTargetFrameRate, so re-read it each frame (one property read).
    if (!this.explicitBudget) {
      const hz = this.state.session?.frameRate;
      if (typeof hz === 'number' && hz > 0 && hz !== this.budgetRateHz) {
        this.budgetRateHz = hz;
        this.frameBudgetMs = 1000 / hz;
      }
    }

    const now = performance.now();
    if (this.lastFrameTime === 0) {
      this.lastFrameTime = now;
      return;
    }

    const frameTime = now - this.lastFrameTime;
    this.lastFrameTime = now;

    if (frameTime > this.frameBudgetMs) {
      this.consecutiveOvershoots++;

      if (this.consecutiveOvershoots >= OVERSHOOT_FRAMES) {
        this.events.emit('xr:perf:warning' as any, {
          frameTimeMs: frameTime,
          budgetMs: this.frameBudgetMs,
          consecutiveOvershoots: this.consecutiveOvershoots,
        });

        if (this.autoScale && this.renderScale > 0.5) {
          this.renderScale -= 0.1;
          this._applyRenderScale();
          Logger.warn('XRPerf', `Auto-reduced render scale target to ${this.renderScale.toFixed(1)}`);
        }

        this.consecutiveOvershoots = 0;
      }
    } else {
      this.consecutiveOvershoots = 0;

      // Gradually restore render scale — only call API when value actually changes
      if (this.autoScale && this.renderScale < 1.0) {
        const newScale = Math.min(1.0, this.renderScale + 0.02);
        if (newScale !== this.renderScale) {
          this.renderScale = newScale;
          this._applyRenderScale();
        }
      }
    }
  }

  /**
   * Push `renderScale` to the renderer. three r185's
   * `XRManager.setFramebufferScaleFactor` only STORES the value while presenting
   * and `warn()`s "Cannot change framebuffer scale while presenting" — the XR
   * layer is sized once in setSession. The old per-frame call here (the restore
   * ramp fired every frame) flooded the headset console with warnings and
   * changed nothing; the value now reaches the renderer only outside a session
   * and is exposed live via `currentRenderScale` / `xr:perf:warning`.
   */
  private _applyRenderScale(): void {
    const xr = this.renderer.native.xr;
    if (xr.isPresenting) {
      if (!this.warnedScaleNoop) {
        this.warnedScaleNoop = true;
        Logger.debug('XRPerf', 'Framebuffer scale cannot change mid-session (three r185); exposing target via currentRenderScale only');
      }
      return;
    }
    xr.setFramebufferScaleFactor(this.renderScale);
  }

  /**
   * Get current frame budget.
   */
  get budget(): number {
    return this.frameBudgetMs;
  }

  /**
   * Get current render scale.
   */
  get currentRenderScale(): number {
    return this.renderScale;
  }
}
